import { type CSSProperties, useEffect, useRef, useState } from 'react';

import DOMPurify from 'dompurify';

import { tiptapBlockMarginBottom, tiptapBlockMarginTop } from './editorStyles';

let mermaidInitialized = false;

const getMermaid = async () => {
  const { default: mermaid } = await import('mermaid');
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'dark',
      fontFamily: 'var(--encore-bodyMono-font-stack, monospace)',
    });
    mermaidInitialized = true;
  }
  return mermaid;
};

const baseStyle: CSSProperties = {
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,
  padding: 'var(--encore-spacing-looser)',
  borderRadius: 'var(--encore-corner-radius-larger)',
  overflow: 'auto',
  backgroundColor: '#333333',
};

const containerStyle: CSSProperties = {
  ...baseStyle,
  display: 'flex',
  justifyContent: 'center',
};

const errorStyle: CSSProperties = {
  ...baseStyle,
  color: '#ffdcd7',
  fontFamily: 'var(--encore-bodyMono-font-stack, monospace)',
  fontSize: 'var(--encore-text-size-smaller-2)',
  whiteSpace: 'pre-wrap',
};

let renderCounter = 0;

export const MermaidRenderer = ({ code }: { code: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!code.trim()) {
      return () => {
        cancelled = true;
      };
    }

    const id = `mermaid-${++renderCounter}`;

    getMermaid()
      .then(mermaid => mermaid.render(id, code.trim()))
      .then(({ svg }) => {
        if (cancelled) return;
        setError(null);
        if (containerRef.current) {
          const clean = DOMPurify.sanitize(svg, {
            USE_PROFILES: { svg: true, svgFilters: true },
            ADD_TAGS: ['foreignObject'],
          });
          containerRef.current.textContent = '';
          const wrapper = document.createElement('div');
          wrapper.innerHTML = clean;
          const svgEl = wrapper.firstElementChild;
          if (svgEl) {
            containerRef.current.appendChild(svgEl);
          }
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to render diagram',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (error) {
    return <div style={errorStyle}>{error}</div>;
  }

  return <div style={containerStyle} ref={containerRef} />;
};
